/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * Template Layout Generator
 */
define([
	'text!./layout/templates/listing.json',
	'../app/nls/StringResources'
], (templateListing, stringResources) => {

	class TemplateLayoutGenerator {
		constructor(options = {}) {
			this._templates = [];
			this.stringResources = options.stringResources || stringResources;
			this._dashboardTemplatesApi = options.dashboardTemplatesApi;
			this._processTemplates(templateListing, options.excludeTemplates);
			this._addCustomTemplates(options.customTemplates);
		}

		initialize() {
			return this._generateTemplates().then(loadedTemplates => this._loadedTemplates = loadedTemplates);
		}

		getTemplates(navTemplate) {
			return this._loadedTemplates.filter(template => template.navTemplates.includes(navTemplate));
		}


		customTemplateDelete(templateId) {
			this._loadedTemplates = this._loadedTemplates.filter(template => template.templateId !== templateId);
		}

		getSelectedLayoutSpec(templateId) {
			const template = this._loadedTemplates.find(template => template.templateId === templateId);
			if (template && template.layoutSpec) {
				return Promise.resolve(template.layoutSpec);
			}
			return Promise.reject(new Error(this.stringResources.get('errorLoadingLayoutFile')));
		}

		_addCustomTemplates(customTemplates = []){
			customTemplates.forEach(customTemplate => {
				this._templates.push({
					label: customTemplate.defaultName,
					ariaLabel: customTemplate.defaultName,
					navigationTemplates: 'custom',
					location: customTemplate.location,
					name: customTemplate.id
				});
			});
		}

		_processTemplates(templates, excludeTemplates) {
			const templateListObj = typeof templates === 'string' ? JSON.parse(templates) : templates;
			let templateList = templateListObj.templates;
			if (excludeTemplates) {
				templateList = templateList.filter(item => !excludeTemplates.includes(item.name));
			}
			for (let i = 0; i < templateList.length; i++) {
				const template = templateList[i];
				if (template.label) {
					template.label = this.stringResources.get(template.label);
				}

				if (template.name) {
					template.ariaLabel = this.stringResources.get(template.name);
				}

				if (template.description) {
					template.description = this.stringResources.get(template.description);
				}
				this._templates.push(template);
			}
		}

		_generateTemplates() {
			const loadedTemplates = [];
			for (let i = 0; i < this._templates.length; i++) {
				if (this._templates[i].navigationTemplates.includes('custom')) {
					loadedTemplates.push(this._generateCustomTemplate(this._templates[i]));
				} else {
					loadedTemplates.push(this._generateRegularTemplate(this._templates[i]));
				}
			}
			return Promise.all(loadedTemplates);
		}

		_generateCustomTemplate(layout) {
			const contextMenuPermissions = ['write', 'execute'];
			return this._dashboardTemplatesApi.getThumbnail(layout.name).then(thumbnail => {
				return this._dashboardTemplatesApi.getTemplate(layout.name).then(template => {
					const contextMenuEnabled = (template.permissions || []).filter(value => contextMenuPermissions.includes(value)).length;
					return {
						templateId: layout.name,
						ariaLabel: layout.ariaLabel,
						navTemplates: layout.navigationTemplates,
						layoutSpec: { thumbnail, layout, contextMenuEnabled, template }
					};
				});
			});
		}

		_generateRegularTemplate(template) {
			return this._getLayoutSpec(template).then(({ layout }) => {
				return {
					templateId: template.name,
					ariaLabel: template.ariaLabel,
					navTemplates: template.navigationTemplates,
					layoutSpec: { layout }
				};
			});
		}

		_getLayoutSpec (template) {
			if (template.navigationTemplates.includes('custom')) {
				return Promise.resolve({ layout: template });
			}
			return new Promise((resolve, reject) => {
				try {
					let requireFilePath = `text!${template.path}/${template.name}.json`;
					require([requireFilePath], (layoutFileContent) => {
						let layout = (typeof layoutFileContent === 'string') ? JSON.parse(layoutFileContent) : layoutFileContent;
						resolve({ layout });
					});
				} catch(error) {
					reject(error);
				}
			});
		}
	}

	return TemplateLayoutGenerator;
});
